// widget/src/hooks/useMobileSheet.ts
//
// Mobile-only behaviors for the active chat sheet. No-ops on desktop and
// while the chat is closed.
//
//   1. Body scroll lock. The position:fixed technique is the only reliable
//      way to stop iOS Safari rubber-band-scrolling the storefront behind
//      the chat (overflow:hidden on body does NOT hold on iOS). We stash
//      the scroll position and restore it on release.
//
//   2. Keyboard tracking. When the soft keyboard opens, visualViewport
//      shrinks but the layout viewport — and our position-fixed host —
//      does not, which would leave the composer hidden behind the
//      keyboard. We publish the keyboard height as `--pearl-kb-inset` on
//      :root; the sheet CSS reads it to lift its bottom edge above the
//      keyboard.

import { useEffect } from "react"

const KB_VAR = "--pearl-kb-inset"
// Visual-viewport geometry. The mobile overlay is sized to these so it always
// covers exactly the visible region above the soft keyboard — the input
// pinned to its bottom is then already on-screen and iOS has no reason to
// scroll (push) the page to reveal it.
const VVH_VAR = "--pearl-vvh"
const VVT_VAR = "--pearl-vvt"

export function useMobileSheet(active: boolean): void {
  useEffect(() => {
    if (!active) return
    if (typeof window === "undefined") return
    if (!window.matchMedia?.("(max-width: 767px)").matches) return

    const root = document.documentElement
    const body = document.body

    // --- scroll lock (position:fixed technique) ---
    const scrollY = window.scrollY
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      htmlOverflow: root.style.overflow,
    }
    body.style.position = "fixed"
    body.style.top = `-${scrollY}px`
    body.style.left = "0"
    body.style.right = "0"
    body.style.width = "100%"
    // Belt-and-suspenders: some iOS versions still rubber-band the document
    // element even with a fixed body. Lock it too.
    root.style.overflow = "hidden"

    // --- keyboard / visual-viewport tracking ---
    // rAF-throttled. iOS Safari fires multiple visualViewport.resize
    // events while the soft keyboard slides up — each setProperty()
    // here updates the CSS variable, which in turn restarts any CSS
    // transition driven by that variable (e.g. `.root.open { height:
    // var(--pearl-vvh) }` with a 0.28s height transition). Without
    // throttling, the in-flight transition gets cancelled + replayed
    // from a mid-interpolated value on every intermediate resize event,
    // producing a visible "snap up then settle back down" — caught
    // 2026-06-03 on the second+ keyboard open (the first open is
    // smooth because Safari emits fewer events on cold start).
    // Coalescing to one update per frame keeps the transition
    // monotonic across the whole keyboard animation.
    const vv = window.visualViewport
    let rafId: number | null = null
    function flush() {
      rafId = null
      if (!vv) return
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      root.style.setProperty(KB_VAR, `${inset}px`)
      root.style.setProperty(VVH_VAR, `${vv.height}px`)
      root.style.setProperty(VVT_VAR, `${vv.offsetTop}px`)
    }
    function scheduleUpdate() {
      if (rafId !== null) return
      rafId = requestAnimationFrame(flush)
    }
    // Initial sync — synchronous so the first paint already has the
    // correct geometry. Subsequent updates go through rAF.
    flush()
    vv?.addEventListener("resize", scheduleUpdate)
    vv?.addEventListener("scroll", scheduleUpdate)

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      vv?.removeEventListener("resize", scheduleUpdate)
      vv?.removeEventListener("scroll", scheduleUpdate)
      root.style.removeProperty(KB_VAR)
      root.style.removeProperty(VVH_VAR)
      root.style.removeProperty(VVT_VAR)
      body.style.position = prev.position
      body.style.top = prev.top
      body.style.left = prev.left
      body.style.right = prev.right
      body.style.width = prev.width
      root.style.overflow = prev.htmlOverflow
      window.scrollTo(0, scrollY)
    }
  }, [active])
}
